import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import PERM from 'constants/core/perm.const';
import { useAccessIds, useCustomPermission } from 'providers/Access.provider';
import { selectAccessHasLockToken } from 'selectors/access.selectors';

/**
 * Time-off permission flags.
 *
 * The backend assigns every stylist the default permission IDs and only
 * includes a permission ID in the lock token when the value is granted
 * (`all_staff` / `personal`). A `no_access` value is represented by the
 * permission ID being absent from the token, so we treat any missing ID
 * as "denied" for any user that has a lock token at all.
 *
 * When there is no lock token (e.g. the master/manager session without a
 * PIN), permissions are not applied and every check defaults to allowed.
 */
export const useTimeOffPermissions = () => {
  const hasLockToken = useSelector(selectAccessHasLockToken);

  const canCreateAll = useCustomPermission(PERM.canCreateTimeOff);
  const canCreatePersonal = useCustomPermission(PERM.canCreateTimeOffPersonal);

  const canDeleteAll = useCustomPermission(PERM.canDeleteTimeOff);
  const canDeletePersonal = useCustomPermission(PERM.canDeleteTimeOffPersonal);

  const canEditDurAll = useCustomPermission(PERM.canEditTimeOffDuration);
  const canEditDurPersonal = useCustomPermission(PERM.canEditTimeOffDurationPersonal);

  const { accessStylistId } = useAccessIds();

  return useMemo(
    () => ({
      accessStylistId,
      permsActive: hasLockToken,
      isCreateBlocked: hasLockToken && !canCreateAll && !canCreatePersonal,
      isCreatePersonalOnly: hasLockToken && canCreatePersonal && !canCreateAll,
      isDeleteBlocked: hasLockToken && !canDeleteAll && !canDeletePersonal,
      isDeletePersonalOnly: hasLockToken && canDeletePersonal && !canDeleteAll,
      isEditDurationBlocked: hasLockToken && !canEditDurAll && !canEditDurPersonal,
      isEditDurationPersonalOnly: hasLockToken && canEditDurPersonal && !canEditDurAll,
    }),
    [accessStylistId, canCreateAll, canCreatePersonal, canDeleteAll, canDeletePersonal, canEditDurAll, canEditDurPersonal, hasLockToken],
  );
};
